import Element, { ElementType } from '@/modules/elements/models/Element'
import User from '@/modules/user/models/User'

type ContactCardUserOptions = {
  alwaysIncludeUserId?: string
}

const pendingContactCardUserIdsByRootEntity: Record<string, string[]> = {}

export function addPendingContactCardUserIds(rootEntityId: string, userIds: string[]) {
  const current = pendingContactCardUserIdsByRootEntity[rootEntityId] ?? []
  const currentSet = new Set(current)
  const toAdd = userIds.filter(userId => !currentSet.has(userId))
  if (!toAdd.length) {
    return
  }
  pendingContactCardUserIdsByRootEntity[rootEntityId] = [...current, ...toAdd]
}

export function removePendingContactCardUserIds(rootEntityId: string, userIds: string[]) {
  const current = pendingContactCardUserIdsByRootEntity[rootEntityId] ?? []
  const userIdsSet = new Set(userIds)
  const filtered = current.filter(id => !userIdsSet.has(id))
  if (filtered.length) {
    pendingContactCardUserIdsByRootEntity[rootEntityId] = filtered
  } else {
    delete pendingContactCardUserIdsByRootEntity[rootEntityId]
  }
}

export function clearPendingContactCardUserIds(rootEntityId: string) {
  delete pendingContactCardUserIdsByRootEntity[rootEntityId]
}

function getPendingContactCardUserIds(rootEntityId: string): string[] {
  return pendingContactCardUserIdsByRootEntity[rootEntityId] ?? []
}

export function getContactCardUserIdsOnPage(
  elements: Element[],
  rootEntityId: string,
  options?: ContactCardUserOptions
): string[] {
  const usedUserIds = elements
    .filter(element => element.rootEntityId === rootEntityId && element.type === ElementType.contactcard)
    .map(element => element.contactCard?.userId)
    .filter((userId): userId is string => !!userId)

  if (!options?.alwaysIncludeUserId) {
    return usedUserIds
  }

  return usedUserIds.filter(userId => userId !== options.alwaysIncludeUserId)
}

export function getUsedContactCardUserIds(
  elements: Element[],
  rootEntityId: string,
  options?: ContactCardUserOptions
): string[] {
  const usedUserIds = getContactCardUserIdsOnPage(elements, rootEntityId, options)
  const pendingUserIds = getPendingContactCardUserIds(rootEntityId)
  const pendingFiltered = options?.alwaysIncludeUserId
    ? pendingUserIds.filter(userId => userId !== options.alwaysIncludeUserId)
    : pendingUserIds
  return [...new Set([...usedUserIds, ...pendingFiltered])]
}

export function getContactCardExcludeUserIds(
  elements: Element[],
  rootEntityId: string,
  currentContactUserId?: string
): string[] {
  return getUsedContactCardUserIds(elements, rootEntityId, {
    alwaysIncludeUserId: currentContactUserId
  })
}

export function getSelectableContactUserIds(teamMemberIds: string[], currentUserId?: string): string[] {
  if (currentUserId && !teamMemberIds.includes(currentUserId)) {
    return [currentUserId, ...teamMemberIds]
  }
  return teamMemberIds
}

export function getSelectableContactUsers(teamMembers: User[], currentUser?: User): User[] {
  if (!currentUser || teamMembers.some(member => member.id === currentUser.id)) {
    return teamMembers
  }
  return [currentUser, ...teamMembers]
}

export function pickAvailableContactUserId(
  preferredUserId: string,
  teamMemberIds: string[],
  usedUserIds: string[]
): string | undefined {
  const usedUserIdsSet = new Set(usedUserIds)
  if (!usedUserIdsSet.has(preferredUserId)) {
    return preferredUserId
  }
  return teamMemberIds.find(userId => !usedUserIdsSet.has(userId))
}
